import { Link } from "react-router-dom";
import PageLayout from "@/components/aoria/PageLayout";
import PageHeader from "@/components/aoria/PageHeader";
import Seo from "@/components/aoria/Seo";

/**
 * Page Manifeste — page de vision, mise en valeur par la typographie,
 * la hiérarchie et l'espace, plus jamais par un grand aplat de couleur.
 *
 * Le bleu institutionnel reste réservé aux éléments d'accent (filets,
 * éditorial-num, italique du nom "L'équité…") et au footer.
 */
const CORE_IDEAS = [
    {
        n: "01",
        keyline: "L'intelligence artificielle est aussi",
        emphasis: "un outil d'accessibilité cognitive",
        body:
            "L'usage raisonné des outils d'IA peut réduire des obstacles cognitifs concrets, comprendre un document, simplifier une démarche, structurer une idée, préparer une réponse. Refuser d'en débattre, c'est laisser le sujet à d'autres et accepter que la maîtrise de ces outils reste un privilège.",
    },
    {
        n: "02",
        keyline: "L'équité suppose parfois",
        emphasis: "des outils différents",
        body:
            "Donner à tout le monde la même chose n'est pas équitable. L'équité, c'est donner à chacun ce qui lui permet de participer pleinement. Pour certaines personnes, à certains moments, cela suppose des outils complémentaires, et c'est légitime.",
    },
];

const ARTICLES = [
    "L'accessibilité cognitive est une condition d'équité, et non un avantage particulier.",
    "Les outils d'intelligence artificielle peuvent réduire certains obstacles cognitifs lorsqu'ils sont utilisés avec lucidité.",
    "L'autonomie ne se mesure pas à l'absence d'outils, mais à la capacité à choisir les siens.",
    "Apprendre à utiliser ces outils fait partie de l'éducation au monde tel qu'il est, et non d'un confort optionnel.",
    "Le rôle des institutions est d'organiser l'usage, pas de l'interdire par principe.",
    "L'humain reste le décideur. La technologie est un appui, jamais une délégation de conscience.",
];

export default function Manifeste() {
    return (
        <PageLayout testid="manifeste-page">
            <Seo
                title="Manifeste · La vision d'Aoria sur l'accessibilité cognitive"
                description="Le manifeste d'Aoria : deux idées centrales et six articles fondateurs. L'intelligence artificielle comme outil d'accessibilité cognitive, l'équité par des outils différents, l'autonomie comme capacité à choisir."
                path="/manifeste"
            />

            <PageHeader
                testid="manifeste"
                eyebrow="MANIFESTE"
                title="L'accessibilité cognitive"
                italic="ne devrait pas être un privilège"
                lead="Aoria n'est ni un parti, ni un lobby, ni un club. C'est une plateforme de réflexion et de sensibilisation, dont le rôle est de rendre lisible un sujet qui ne l'est pas encore assez : l'accessibilité cognitive à l'ère de l'intelligence artificielle."
                backdrop="flows"
            />

            {/* Two central ideas — traitement éditorial premium, fond clair,
                mise en valeur par la typographie et les filets, pas par la couleur */}
            <section
                data-testid="manifeste-core-ideas"
                className="py-24 md:py-32 border-t border-[#E0E1DD]"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
                    <div className="reveal flex items-center gap-4 mb-10">
                        <span
                            aria-hidden="true"
                            className="inline-block h-px w-9 bg-[#0D1B2A]/40"
                        />
                        <span className="eyebrow">Deux idées centrales</span>
                    </div>
                    <h2 className="reveal delay-1 h2 text-3xl md:text-4xl lg:text-5xl text-[#0D1B2A] leading-tight max-w-4xl">
                        Tout part de deux convictions{" "}
                        <span className="italic text-[#415A77]">
                            que nous voulons assumer
                        </span>
                        .
                    </h2>

                    <div className="mt-20 md:mt-28 space-y-20 md:space-y-28">
                        {CORE_IDEAS.map((idea, i) => (
                            <figure
                                key={idea.n}
                                data-testid={`manifeste-idea-${i}`}
                                className="reveal grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start"
                            >
                                <div className="lg:col-span-3">
                                    <div className="flex items-baseline gap-4">
                                        <span
                                            aria-hidden="true"
                                            className="inline-block h-px w-6 bg-[#0D1B2A]/40"
                                        />
                                        <span className="editorial-num text-2xl text-[#0D1B2A]">
                                            {idea.n}
                                        </span>
                                    </div>
                                </div>
                                <blockquote className="lg:col-span-9 border-l border-[#0D1B2A] pl-8 md:pl-12 max-w-3xl">
                                    <p className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.12] text-[#0D1B2A] tracking-[-0.01em]">
                                        {idea.keyline}{" "}
                                        <span className="italic text-[#415A77]">
                                            {idea.emphasis}
                                        </span>
                                        .
                                    </p>
                                    <figcaption className="mt-8 text-[#1B263B] text-base md:text-lg leading-relaxed max-w-2xl not-italic">
                                        {idea.body}
                                    </figcaption>
                                </blockquote>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* Six founding articles — fond crème très subtil (#F4F5F7) pour
                créer un rythme sans rupture chromatique */}
            <section
                data-testid="manifeste-articles"
                className="py-24 md:py-32 bg-[#F4F5F7] border-y border-[#E0E1DD]"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-14">
                        <div className="lg:col-span-3">
                            <div className="reveal flex items-center gap-4">
                                <span
                                    aria-hidden="true"
                                    className="inline-block h-px w-9 bg-[#0D1B2A]/40"
                                />
                                <span className="eyebrow">
                                    Six articles fondateurs
                                </span>
                            </div>
                        </div>
                        <div className="lg:col-span-9 max-w-3xl">
                            <h2 className="reveal delay-1 h2 text-3xl md:text-4xl text-[#0D1B2A] leading-tight">
                                Ce que défend Aoria,{" "}
                                <span className="italic text-[#415A77]">
                                    sans ambiguïté
                                </span>
                                .
                            </h2>
                        </div>
                    </div>

                    <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
                        {ARTICLES.map((a, i) => (
                            <li
                                key={a}
                                data-testid={`manifeste-article-${i}`}
                                className="reveal border-t border-[#0D1B2A]/80 pt-5"
                            >
                                <span className="editorial-num text-sm">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <p className="mt-3 font-serif text-lg md:text-xl leading-snug text-[#0D1B2A]">
                                    {a}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* « Ce que cela implique concrètement », relier principes et actions */}
            <section
                data-testid="manifeste-implications"
                className="py-24 md:py-32 border-b border-[#E0E1DD]"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-14">
                        <div className="lg:col-span-3">
                            <div className="reveal flex items-center gap-4">
                                <span
                                    aria-hidden="true"
                                    className="inline-block h-px w-9 bg-[#0D1B2A]/40"
                                />
                                <span className="eyebrow">
                                    Ce que cela implique concrètement
                                </span>
                            </div>
                        </div>
                        <div className="lg:col-span-9 max-w-3xl">
                            <h2 className="reveal delay-1 h2 text-3xl md:text-4xl text-[#0D1B2A] leading-tight">
                                Des principes,{" "}
                                <span className="italic text-[#415A77]">
                                    et ce qu'ils engagent
                                </span>
                                .
                            </h2>
                        </div>
                    </div>

                    <ol className="divide-y divide-[#E0E1DD] border-y border-[#E0E1DD]">
                        {[
                            {
                                title: "Rendre les contenus plus compréhensibles",
                                body: "Travailler avec les acteurs publics et privés pour des contenus, formulaires et interfaces qui ne supposent pas une charge cognitive disproportionnée.",
                            },
                            {
                                title: "Favoriser les usages raisonnés de l'IA",
                                body: "Documenter ce que l'intelligence artificielle peut faire, ce qu'elle ne fera pas, et ce qui suppose dans tous les cas une vigilance humaine.",
                            },
                            {
                                title: "Documenter les bonnes pratiques",
                                body: "Faire émerger des repères opérationnels, validés par l'usage, qui aident à organiser plutôt qu'à interdire, dans l'enseignement, en entreprise, dans les administrations.",
                            },
                            {
                                title: "Soutenir l'accès aux ressources",
                                body: "Publier en libre accès, en français, sans contrepartie commerciale, et faciliter l'appropriation de ces ressources par celles et ceux qui en ont l'usage.",
                            },
                            {
                                title: "Contribuer au débat public",
                                body: "Apporter une voix sobre, fondée et indépendante dans les discussions sur le numérique, l'éducation, le travail et l'accessibilité cognitive.",
                            },
                        ].map((item, i) => (
                            <li
                                key={item.title}
                                data-testid={`manifeste-implication-${i}`}
                                className="py-8 md:py-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10"
                            >
                                <div className="md:col-span-2">
                                    <span className="editorial-num text-2xl text-[#0D1B2A]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <div className="md:col-span-10 max-w-3xl">
                                    <h3 className="h3 text-xl md:text-2xl text-[#0D1B2A]">
                                        {item.title}
                                    </h3>
                                    <p className="mt-3 text-[#1B263B] text-base md:text-lg leading-relaxed">
                                        {item.body}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>

                    <div className="mt-16 md:mt-20 flex flex-col sm:flex-row gap-4">
                        <Link
                            to="/enjeu"
                            data-testid="manifeste-cta-enjeux"
                            className="btn-secondary rounded-full inline-block px-7 py-3.5 text-sm tracking-[0.15em] uppercase"
                        >
                            Lire les enjeux
                        </Link>
                        <Link
                            to="/ressources"
                            data-testid="manifeste-cta-ressources"
                            className="btn-primary rounded-full inline-block px-7 py-3.5 text-sm tracking-[0.15em] uppercase"
                        >
                            Voir les ressources
                        </Link>
                    </div>
                </div>
            </section>
        </PageLayout>
    );
}
